import { differenceInDays, isBefore, sub, subMonths } from 'date-fns';
// eslint-disable-next-line import/no-unresolved
import { LayoutAxis } from 'plotly.js-basic-dist';
import { NO_DATA } from '../../../Base.constants';
import { CqsMetric, CreMetric, HistoricalRecord } from '../../../models/RiskProducts';
import { DATE_FORMATS, formatDate, parseDate } from '../../../utils/dateUtils';
import { hasCqsMetrics } from './CqsChart/CqsChart.logic';
import { hasCreMetrics } from './CreChart/CreChart.logic';
import { STARTING_DATE_RANGE } from './RiskProductsWidget.logic.constants';

const oneDayInMs = 1000 * 60 * 60 * 24;

export const hasCompanyRiskMetrics = (cqsMetrics: CqsMetric[], creMetrics: CreMetric[]): boolean => {
  return hasCqsMetrics(cqsMetrics) || hasCreMetrics(creMetrics);
};

// ------------ Filter risk metrics ------------

export const filterRiskMetricsByYearRange = (riskMetrics: HistoricalRecord[]): HistoricalRecord[] => {
  const startingDate = riskMetricsStartingDate(riskMetrics[0].calculation_date);
  const singleRiskMetricBeforeStartingDate = riskMetrics.find((metric) =>
    isBefore(parseDate(metric.calculation_date, DATE_FORMATS.YEAR_MONTH_DAY), startingDate)
  );
  const riskMetricsAfterStartingDate = riskMetrics.filter((metric) =>
    isBefore(startingDate, parseDate(metric.calculation_date, DATE_FORMATS.YEAR_MONTH_DAY))
  );

  // 2 year + 1 week range is needed to avoid empty space at the start of the chart
  return singleRiskMetricBeforeStartingDate
    ? riskMetricsAfterStartingDate.concat(singleRiskMetricBeforeStartingDate)
    : riskMetricsAfterStartingDate;
};

export const riskMetricsStartingDate = (latestRiskMetricDate: string): Date => {
  const latestRiskMetricISODate = parseDate(latestRiskMetricDate, DATE_FORMATS.YEAR_MONTH_DAY);
  return sub(latestRiskMetricISODate, { years: STARTING_DATE_RANGE });
};

export const getMetricTag = (value?: string | number | null): string => value?.toString() ?? NO_DATA;

// ------------ Years Axis Block ------------

export const computeYearsAxisData = (latestRiskMetricDate: string): Partial<LayoutAxis> => {
  const yearAxisTickValues = computeYearsAxisTickValues(latestRiskMetricDate);
  return {
    tickvals: yearAxisTickValues,
    ticktext: computeYearsAxisTickKeys(latestRiskMetricDate),
    range: computeYearsAxisValuesRange(yearAxisTickValues, latestRiskMetricDate),
  };
};

const computeYearsAxisTickValues = (latestRiskMetricDate: string): number[] => {
  const startingDate = riskMetricsStartingDate(latestRiskMetricDate);
  const latestDate = parseDate(latestRiskMetricDate, DATE_FORMATS.YEAR_MONTH_DAY);
  latestDate.setUTCDate(1);
  const differenceInMs = latestDate.getTime() - startingDate.getTime();
  const daysBetweenDates = differenceInMs / oneDayInMs;
  const yearAxisTicks = STARTING_DATE_RANGE * 2;

  const tickInDays = Math.floor(daysBetweenDates / yearAxisTicks);
  const remainingDays = daysBetweenDates % yearAxisTicks;
  const valuesRange: number[] = [];
  for (let i = 0; i < yearAxisTicks; i += 1) {
    const lastValue = valuesRange[0] || 0;
    const additionalDays = i === 0 ? tickInDays + remainingDays : tickInDays;
    valuesRange.unshift(lastValue + additionalDays);
  }
  return valuesRange;
};

const computeYearsAxisTickKeys = (latestRiskMetricDate: string): string[] => {
  const latestDate = parseDate(latestRiskMetricDate, DATE_FORMATS.YEAR_MONTH_DAY);
  latestDate.setUTCDate(1);

  const keysRange = [formatDate(latestDate, DATE_FORMATS.MONTH_YEAR_MLA)];
  const remainingTickCount = STARTING_DATE_RANGE * 2 - 1;
  for (let i = 0; i < remainingTickCount; i += 1) {
    const lastDateStr = keysRange[keysRange.length - 1];
    const parsedDate = parseDate(lastDateStr, DATE_FORMATS.MONTH_YEAR_MLA);
    const newDate = subMonths(parsedDate, 6);
    keysRange.push(formatDate(newDate, DATE_FORMATS.MONTH_YEAR_MLA));
  }
  return keysRange;
};

const computeYearsAxisValuesRange = (yearAxisTickValues: number[], latestRiskMetricDate: string): number[] => {
  const latestDate = parseDate(latestRiskMetricDate, DATE_FORMATS.YEAR_MONTH_DAY);
  return [0, yearAxisTickValues[0] + latestDate.getDate()];
};

export const mapRiskMetricsToYearsAxisValues = (riskMetrics: CqsMetric[] | CreMetric[]): number[] => {
  const startingDate = riskMetricsStartingDate(riskMetrics[0].calculationDate);
  return riskMetrics.map((metric) =>
    differenceInDays(parseDate(metric.calculationDate, DATE_FORMATS.YEAR_MONTH_DAY), startingDate)
  );
};

// ------------ Tooltip ------------
export const getTooltipRiskMetricData = <T extends CqsMetric | CreMetric>(metrics: T[], pointNumber: number): T => {
  const hoveredRiskMetricDate = metrics[pointNumber].calculationDate;
  return metrics?.filter((riskMetric) => riskMetric.calculationDate === hoveredRiskMetricDate)?.[0];
};

export const sortMetricsDescByCalculationDate = <T extends CqsMetric | CreMetric>(metrics: T[]): T[] => {
  return [...metrics].sort((a, b) => new Date(b.calculationDate).valueOf() - new Date(a.calculationDate).valueOf());
};
